import axios, {
  AxiosError,
  type InternalAxiosRequestConfig,
} from 'axios';

import { apiBaseUrl } from '../config';
import { clearSession, getValidAccessToken } from './oidc';

function isApiRequest(url: string | undefined): boolean {
  if (!url) return false;
  // Only attach the bearer token to requests that target our own API.
  // Presigned S3 PUT uploads go to a different host and must NOT receive an
  // Authorization header (it would break the request signature).
  return url.startsWith(apiBaseUrl);
}

/**
 * Registers global axios interceptors:
 * - attaches the OIDC access token as a Bearer header on API requests,
 *   refreshing it first when it is missing or about to expire
 * - on 401 responses, clears the session and redirects to the login page
 */
export function setupAxiosAuth(): void {
  axios.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      if (isApiRequest(config.url)) {
        const token = await getValidAccessToken();
        if (token) {
          config.headers.set('Authorization', `Bearer ${token}`);
        }
      }
      return config;
    },
  );

  axios.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      if (
        error.response?.status === 401 &&
        isApiRequest(error.config?.url) &&
        window.location.pathname !== '/login'
      ) {
        clearSession();
        window.location.href = '/login';
      }
      return Promise.reject(error);
    },
  );
}
